import { useEffect, useCallback, useRef } from 'react'
import { message } from 'antd'
import { useAppDispatch, useAppSelector } from './redux'
import { logout } from '../store/slices/authSlice'
import { useLanguage } from '../contexts/LanguageContext'
import {
  isCustomerAccount,
  setLastActivityAt,
  clearLastActivityAt,
  isSessionExpired,
} from '../utils/sessionTimeout'

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'] as const
const ACTIVITY_THROTTLE_MS = 30_000
const CHECK_INTERVAL_MS = 60_000

export function useSessionTimeout() {
  const dispatch = useAppDispatch()
  const { isAuthenticated, user } = useAppSelector((state) => state.auth)
  const { t } = useLanguage()
  const throttleRef = useRef(0)

  const forceLogout = useCallback(() => {
    clearLastActivityAt()
    dispatch(logout())
    message.warning(t('auth.sessionExpired'))
    window.location.href = '/login'
  }, [dispatch, t])

  const checkExpired = useCallback(() => {
    if (!isAuthenticated || !user || !isCustomerAccount(user.accountType)) return
    if (isSessionExpired()) {
      forceLogout()
    }
  }, [isAuthenticated, user, forceLogout])

  useEffect(() => {
    if (!isAuthenticated || !user || !isCustomerAccount(user.accountType)) return

    checkExpired()

    const handleActivity = () => {
      const now = Date.now()
      if (now - throttleRef.current < ACTIVITY_THROTTLE_MS) return
      throttleRef.current = now
      setLastActivityAt(now)
    }

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true })
    })

    const interval = setInterval(checkExpired, CHECK_INTERVAL_MS)

    const handleVisibility = () => {
      if (!document.hidden) {
        checkExpired()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity)
      })
      clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [isAuthenticated, user, checkExpired])
}
